'use client';

/**
 * Action Item List Component
 * Displays and manages action items
 */

import { useState } from 'react';
import {
  ActionItem,
  ActionStatus,
  ACTION_STATUS_CONFIG,
} from '@/lib/one-on-one/types';

interface ActionItemListProps {
  items: ActionItem[];
  onUpdate: (id: string, updates: Partial<ActionItem>) => void;
  onDelete: (id: string) => void;
  onAdd?: (content: string, assignedTo: 'manager' | 'report') => void;
  canEdit?: boolean;
  showMeetingLink?: boolean;
}

export function ActionItemList({
  items,
  onUpdate,
  onDelete,
  onAdd,
  canEdit = true,
  showMeetingLink = false,
}: ActionItemListProps) {
  const [newContent, setNewContent] = useState('');
  const [newAssignee, setNewAssignee] = useState<'manager' | 'report'>('report');

  const handleAdd = () => {
    if (!newContent.trim() || !onAdd) return;
    onAdd(newContent.trim(), newAssignee);
    setNewContent('');
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const isOverdue = (item: ActionItem) => {
    if (!item.due_date || item.status === 'completed' || item.status === 'cancelled') {
      return false;
    }
    return new Date(item.due_date) < new Date();
  };

  return (
    <div className="space-y-3">
      {/* Add new item */}
      {onAdd && canEdit && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="Add action item..."
            className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
          />
          <select
            value={newAssignee}
            onChange={(e) => setNewAssignee(e.target.value as 'manager' | 'report')}
            className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
          >
            <option value="report">Report</option>
            <option value="manager">Manager</option>
          </select>
          <button
            onClick={handleAdd}
            disabled={!newContent.trim()}
            className="px-3 py-2 bg-accent text-accent-foreground rounded text-sm font-medium disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}

      {/* Items list */}
      {items.length === 0 ? (
        <p className="text-sm text-foreground-tertiary text-center py-4">No action items</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const statusConfig = ACTION_STATUS_CONFIG[item.status];
            const overdue = isOverdue(item);

            return (
              <div
                key={item.id}
                className={`
                  flex items-start gap-3 p-3 rounded border transition-colors
                  ${item.status === 'completed'
                    ? 'border-border-muted bg-cream/5'
                    : overdue
                      ? 'border-red-500/30 bg-red-500/5'
                      : 'border-border'
                  }
                `}
              >
                {/* Checkbox */}
                {canEdit && (
                  <button
                    onClick={() => onUpdate(item.id, {
                      status: item.status === 'completed' ? 'pending' : 'completed',
                    })}
                    className={`
                      mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0
                      transition-colors
                      ${item.status === 'completed'
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-border/30 hover:border-accent'
                      }
                    `}
                  >
                    {item.status === 'completed' && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.status === 'completed' ? 'text-foreground-tertiary line-through' : 'text-foreground'}`}>
                    {item.content}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-foreground-tertiary">
                    <span className={item.assigned_to === 'manager' ? 'text-purple-400' : 'text-blue-400'}>
                      {item.assigned_to === 'manager' ? '👤 Manager' : '👤 Report'}
                    </span>
                    {item.due_date && (
                      <span className={overdue ? 'text-red-400' : ''}>
                        Due: {formatDate(item.due_date)}
                        {overdue && ' (overdue)'}
                      </span>
                    )}
                    {item.status !== 'pending' && item.status !== 'completed' && (
                      <span style={{ color: statusConfig.color }}>
                        {statusConfig.icon} {statusConfig.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {canEdit && (
                  <button
                    onClick={() => onDelete(item.id)}
                    className="p-1 text-foreground-muted hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ActionItemList;
